/**
 * Evidence Context
 *
 * Provides app-wide state management for the Click-to-Explain Evidence Modal.
 * Tracks which metric is being explained, the associated run_id, and disclosure level.
 *
 * BUG-UI-061: Added incomeBasis support for gross_income_6m comparison display
 * BUG-UI-112: Auto-close modal on logout/session timeout
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { DisclosureLevel } from '@/types/evidenceContract';
import { EvidenceModal } from '@/components/audit/EvidenceModal';
import useAppState from '@/common/AppState';

/** BUG-UI-061: Income basis metadata for income comparison */
export interface IncomeBasisData {
  income_basis_used: 'ATTESTED' | 'DEPOSITED' | 'VERIFIED';
  income_confidence: number;
  income_sources: {
    attested?: number;
    deposited_net?: number;
    verified_gross?: number;
  };
  selection_reason?: string;
}

/** BUG-UI-120: Dashboard value to sync with modal display */
export interface DashboardValueData {
  value: number;
  formattedValue: string;
  month?: string;  // The month this value is from (e.g., "Oct 2025")
  unit?: 'USD' | 'PERCENT' | 'COUNT';
}

interface EvidenceModalState {
  isOpen: boolean;
  uiFieldPath: string;
  runId: string;
  disclosureLevel: DisclosureLevel;
  memberName?: string;
  /** BUG-UI-092: Member ID for cross-run historical evidence */
  memberId?: string;
  /** BUG-UI-061: Income basis data for gross_income_6m comparison */
  incomeBasis?: IncomeBasisData;
  /** BUG-UI-120: Dashboard value for reconciliation */
  dashboardValue?: DashboardValueData;
  /** Option 3: Initial month to display in modal (from card's selected month) */
  initialMonth?: string | null;
}

interface EvidenceContextType {
  /** Open the evidence modal for a specific metric */
  showEvidence: (params: {
    uiFieldPath: string;
    runId: string;
    disclosureLevel?: DisclosureLevel;
    memberName?: string;
    /** BUG-UI-092: Member ID for cross-run historical evidence */
    memberId?: string;
    /** BUG-UI-061: Income basis data for gross_income_6m */
    incomeBasis?: IncomeBasisData;
    /** BUG-UI-120: Dashboard value for reconciliation */
    dashboardValue?: DashboardValueData;
    /** Option 3: Initial month to display in modal (from card's selected month) */
    initialMonth?: string | null;
  }) => void;
  /** Close the evidence modal */
  hideEvidence: () => void;
  /** Current modal state */
  modalState: EvidenceModalState;
}

const defaultState: EvidenceModalState = {
  isOpen: false,
  uiFieldPath: '',
  runId: '',
  disclosureLevel: 'LENDER',
};

const EvidenceContext = createContext<EvidenceContextType | undefined>(undefined);

export function EvidenceProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<EvidenceModalState>(defaultState);

  // BUG-UI-112: Subscribe to auth state to close modal on logout/timeout
  const isAuthenticated = useAppState((state) => state.isAuthenticated);
  const isLoggedOut = useAppState((state) => state.isLoggedOut);

  // BUG-UI-112: Auto-close modal when user logs out or session times out
  useEffect(() => {
    if (isAuthenticated === 'no' || isLoggedOut) {
      if (modalState.isOpen) {
        console.log('🔒 [EvidenceContext] Closing modal due to logout/session timeout');
        setModalState(prev => ({ ...prev, isOpen: false }));
      }
    }
  }, [isAuthenticated, isLoggedOut, modalState.isOpen]);

  const showEvidence = useCallback(({
    uiFieldPath,
    runId,
    disclosureLevel = 'LENDER',
    memberName,
    memberId,
    incomeBasis,
    dashboardValue,
    initialMonth,
  }: {
    uiFieldPath: string;
    runId: string;
    disclosureLevel?: DisclosureLevel;
    memberName?: string;
    memberId?: string;
    incomeBasis?: IncomeBasisData;
    dashboardValue?: DashboardValueData;
    initialMonth?: string | null;
  }) => {
    console.log('📋 [EvidenceContext] Opening evidence modal', { uiFieldPath, runId, memberId, disclosureLevel, hasIncomeBasis: !!incomeBasis, hasDashboardValue: !!dashboardValue, initialMonth });
    setModalState({
      isOpen: true,
      uiFieldPath,
      runId,
      disclosureLevel,
      memberName,
      memberId,
      incomeBasis,
      dashboardValue,
      initialMonth,
    });
  }, []);

  const hideEvidence = useCallback(() => {
    console.log('📋 [EvidenceContext] Closing evidence modal');
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  return (
    <EvidenceContext.Provider value={{ showEvidence, hideEvidence, modalState }}>
      {children}
      {/* Render the modal at the root level */}
      {/* BUG-UI-014 FIX: Add key prop to force complete remount when metric changes */}
      {/* This ensures useEvidence hook state is fully reset for each factor */}
      {/* BUG-UI-061: Pass incomeBasis for gross_income_6m comparison */}
      {/* BUG-UI-092: Pass memberId for cross-run historical evidence */}
      {/* BUG-UI-120: Pass dashboardValue for reconciliation */}
      {/* Option 3: Pass initialMonth so modal opens at selected month */}
      <EvidenceModal
        key={`${modalState.uiFieldPath}-${modalState.runId}-${modalState.initialMonth || 'latest'}`}
        isOpen={modalState.isOpen}
        onClose={hideEvidence}
        uiFieldPath={modalState.uiFieldPath}
        runId={modalState.runId}
        memberId={modalState.memberId}
        disclosureLevel={modalState.disclosureLevel}
        incomeBasis={modalState.incomeBasis}
        dashboardValue={modalState.dashboardValue}
        initialMonth={modalState.initialMonth}
      />
    </EvidenceContext.Provider>
  );
}

/**
 * Hook to access evidence modal controls
 */
export function useEvidenceModal() {
  const context = useContext(EvidenceContext);
  if (!context) {
    throw new Error('useEvidenceModal must be used within an EvidenceProvider');
  }
  return context;
}

